import React, { useEffect, useState } from 'react';
import { ArrowsClockwise, Bank, Money, CheckCircle } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';

const API = `${window.location.origin}/api`;
const fmt = (v) => (Number(v) || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const half = (ps) => (String(ps) === '01' ? '01' : '16');

// Effective przelew: explicit value on the record > active cyclic rule > 0 (całość gotówką)
export const splitFor = (rec, doWyplaty, rules) => {
  const ym = Number(rec.year) * 100 + Number(rec.month);
  const rule = rules.find((r) => r.employee_id === rec.employee_id && r.period_start === half(rec.period_start) && r.from_ym <= ym);
  const raw = rec.split_przelew ?? rule?.amount ?? 0;
  const przelew = Math.max(0, raw);
  return { przelew, gotowka: doWyplaty - przelew, cyclic: !!rule, fromRule: rec.split_przelew == null && !!rule };
};

const Toggle = ({ on, onClick, label, testid, color = 'bg-emerald-600' }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 select-none"
    data-testid={testid}
    title={label}
  >
    <span className={`relative w-9 h-5 rounded-full transition-colors ${on ? color : 'bg-slate-300'}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-4' : ''}`} />
    </span>
    <span className="hidden md:inline">{label}</span>
  </button>
);

function Row({ rec, doWyplaty, rules, onChanged }) {
  const s = splitFor(rec, doWyplaty, rules);
  const [val, setVal] = useState(rec.split_przelew ?? (s.fromRule ? s.przelew : ''));
  useEffect(() => { setVal(rec.split_przelew ?? (s.fromRule ? s.przelew : '')); }, [rec.split_przelew, s.fromRule, s.przelew]);

  const patch = async (body) => {
    try { await onChanged(rec.id, body); } catch (e) { alert(e.response?.data?.detail || 'Błąd zapisu'); }
  };
  const saveAmount = () => {
    const str = String(val).replace(',', '.').trim();
    if (str === '') return rec.split_przelew != null && patch({ clear_split: true });
    const n = parseFloat(str);
    if (Number.isNaN(n) || n < 0) return alert('Nieprawidłowa kwota');
    if (n !== rec.split_przelew) patch({ split_przelew: n });
  };
  const toggleCyclic = async () => {
    if (!s.cyclic) {
      const str = String(val).replace(',', '.').trim();
      const n = parseFloat(str);
      if (str === '' || Number.isNaN(n)) return alert('Najpierw wpisz kwotę przelewu');
      return patch({ split_przelew: n, cyclic: true });
    }
    patch({ cyclic: false });
  };

  return (
    <tr className="border-b border-slate-100" data-testid={`split-row-${rec.id}`}>
      <td className="py-3 px-3 sm:px-4 font-bold text-slate-800">{rec.employee_name || 'Nieznany'}</td>
      <td className={`py-3 px-3 sm:px-4 text-right font-semibold whitespace-nowrap ${doWyplaty < 0 ? 'text-red-600' : 'text-slate-700'}`} data-testid={`split-total-${rec.id}`} title={doWyplaty < 0 ? 'Ujemna kwota — brak wypłaty' : undefined}>{fmt(doWyplaty)} zł</td>
      <td className="py-3 px-3 sm:px-4">
        <div className="flex items-center gap-2 sm:gap-3 justify-end flex-wrap">
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onBlur={saveAmount}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              placeholder="0,00"
              className="w-24 sm:w-28 text-right border border-slate-300 rounded-md pl-2 pr-7 py-1.5 text-sm font-semibold"
              data-testid={`split-przelew-input-${rec.id}`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">zł</span>
          </div>
          <Toggle on={s.cyclic} onClick={toggleCyclic} label="Cykliczny" color="bg-blue-600" testid={`split-cyclic-${rec.id}`} />
          <Toggle on={!!rec.split_przelew_paid} onClick={() => patch({ split_przelew_paid: !rec.split_przelew_paid })} label="Wypłacone" testid={`split-przelew-paid-${rec.id}`} />
        </div>
      </td>
      <td className="py-3 px-3 sm:px-4">
        <div className="flex items-center gap-2 sm:gap-3 justify-end">
          <span className={`font-bold whitespace-nowrap ${s.gotowka < 0 ? 'text-red-600' : 'text-amber-700'}`} data-testid={`split-gotowka-${rec.id}`}>{fmt(s.gotowka)} zł</span>
          <Toggle on={!!rec.split_gotowka_paid} onClick={() => patch({ split_gotowka_paid: !rec.split_gotowka_paid })} label="Wypłacone" testid={`split-gotowka-paid-${rec.id}`} />
        </div>
      </td>
    </tr>
  );
}

export default function PrzelewGotowkaView({ salaries, getDoWyplaty, onRecordUpdated }) {
  const [rules, setRules] = useState([]);
  const loadRules = () => axios.get(`${API}/payouts/cyclic`).then((r) => setRules(r.data || [])).catch(() => {});
  useEffect(() => { loadRules(); }, []);

  const onChanged = async (id, body) => {
    const r = await axios.patch(`${API}/payouts/${id}`, body);
    onRecordUpdated(r.data);
    if ('cyclic' in body || 'split_przelew' in body) await loadRules();
  };

  const rows = salaries.map((rec) => ({ rec, total: getDoWyplaty(rec), s: splitFor(rec, getDoWyplaty(rec), rules) }));
  const sum = (fn) => rows.reduce((a, r) => a + fn(r), 0);
  const przelewSum = sum((r) => r.s.przelew);
  const gotowkaSum = sum((r) => r.s.gotowka);
  const przelewLeft = sum((r) => (r.rec.split_przelew_paid ? 0 : r.s.przelew));
  const gotowkaLeft = sum((r) => (r.rec.split_gotowka_paid ? 0 : r.s.gotowka));

  return (
    <div className="space-y-4" data-testid="przelew-gotowka-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Suma przelewów', v: przelewSum, Icon: Bank, c: 'text-blue-600', t: 'sum-przelew' },
          { label: 'Suma gotówki', v: gotowkaSum, Icon: Money, c: 'text-amber-600', t: 'sum-gotowka' },
          { label: 'Przelewy do wypłaty', v: przelewLeft, Icon: CheckCircle, c: 'text-slate-700', t: 'left-przelew' },
          { label: 'Gotówka do wypłaty', v: gotowkaLeft, Icon: CheckCircle, c: 'text-slate-700', t: 'left-gotowka' },
        ].map(({ label, v, Icon, c, t }) => (
          <div key={t} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500"><Icon className={`w-4 h-4 ${c}`} weight="bold" /> {label}</div>
            <div className={`text-xl sm:text-2xl font-bold mt-1 ${c}`} data-testid={t}>{fmt(v)} zł</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="text-xs font-bold uppercase tracking-wider text-slate-600 border-b border-slate-200">
              <th className="text-left py-3 px-3 sm:px-4">Skrót</th>
              <th className="text-right py-3 px-3 sm:px-4">Do wypłaty</th>
              <th className="text-right py-3 px-3 sm:px-4"><Bank className="w-4 h-4 inline mr-1" />Przelew</th>
              <th className="text-right py-3 px-3 sm:px-4"><Money className="w-4 h-4 inline mr-1" />Gotówka</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ rec, total }) => (
              <Row key={rec.id} rec={rec} doWyplaty={total} rules={rules} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-1.5">
        <ArrowsClockwise className="w-3.5 h-3.5" /> „Cykliczny” — ta sama kwota przelewu w tym samym okresie (01-15 lub 16-30) w kolejnych miesiącach. Puste pole = całość gotówką.
      </p>
    </div>
  );
}
